import {
  memo, useCallback, useState,
  type FunctionComponent, type Ref, type Component, type MouseEvent, type Dispatch, type SetStateAction
} from 'react';
import size from 'lodash/size';
import { FormattedMessage } from 'react-intl';
import type { ConnectableElement } from 'react-dnd';
// Material UI imports
import Box, { type BoxProps } from '@mui/material/Box';
import Typography from '@mui/material/Typography';
// Skillmore UI Components
import { type Values } from '@empathco/ui-components/src/helpers/intl';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import AlertBox from '@empathco/ui-components/src/mixins/AlertBox';
import useDnD from '@empathco/ui-components/src/hooks/useDnD';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import AddSkillPopover from '@empathco/ui-components/src/widgets/AddSkillPopover';
// local imports
import { LookupItem } from '../models/lookupItem';
import { Skill } from '../models/skill';
import { DraggableType } from '../constants/draggableTypes';
import { SkillGroup } from '../context/persistent';
import TeamSelectedSkills from '../widgets/TeamSelectedSkills';
import TeamSkills from '../widgets/TeamSkills';
import AddSkillsDialog from '../widgets/AddSkillsDialog';
import SkillSearch from '../widgets/SkillSearch';
// SCSS imports
import { sectionHeader } from './SkillsDnDSelector.module.scss';

type SkillsStateType = Record<number, Skill>;

export const SKILL_SELECTOR_JOB = 'job' as const;
export const SKILL_SELECTOR_TALENT = 'talent' as const;

export const SKILL_SELECTOR = [
  SKILL_SELECTOR_JOB,
  SKILL_SELECTOR_TALENT
] as const;
export type SkillSelectorType = typeof SKILL_SELECTOR[number];

type SkillsDnDSelectorProps = {
  readonly variant: SkillSelectorType;
  readonly allSkills: Skill[]; // union of top/current skills + added skills
  readonly skillIds: number[]; // selected skill ids
  readonly setSkillIds: Dispatch<SetStateAction<number[]>>;
  readonly userAddedSkillIds?: number[];
  readonly onAddSkill?: (skill: Skill) => void;
  readonly onAddSkillGroup?: (skillGroup: SkillGroup) => void;
  readonly onDeleteSkill?: (id: number) => void;
  readonly onAddedSkillsChange?: (addedSkillIds: number[]) => void;
  readonly onSelectionChange: (skillIds: number[]) => void;
  readonly onClick?: (id: number, abbr: string) => void;
  readonly onReset?: () => void;
  readonly withLevels?: boolean;
  readonly disabledSkills?: boolean | null;
  readonly disabledSelected?: boolean | null;
  readonly disabledAddSkill?: boolean | null;
  readonly excludeJobIds?: number[] | null;
  readonly excludeOrgIds?: number[] | null;
  readonly title?: string;
  readonly subtitle?: string;
  readonly emptyMessage?: string;
  readonly warning?: string;
  readonly values?: Values;
}

const SkillsDnDSelector: FunctionComponent<SkillsDnDSelectorProps> = ({
  variant,
  allSkills,
  skillIds,
  setSkillIds,
  userAddedSkillIds,
  onAddSkill,
  onAddSkillGroup,
  onDeleteSkill,
  onAddedSkillsChange,
  onSelectionChange,
  onClick,
  onReset,
  withLevels = false,
  disabledSkills = false,
  disabledSelected = false,
  disabledAddSkill = false,
  excludeJobIds,
  excludeOrgIds,
  title,
  subtitle,
  emptyMessage,
  warning,
  values
  // eslint-disable-next-line complexity
}) => {
  const {
    items: skills,
    addedIds,
    availableIds,
    excludeIds,
    addItem,
    deleteItem,
    moveItem,
    unselectItem,
    resetItems,
    // Drag and Drop state
    isDragging,
    drop,
    isActive,
    selectedDrop
  } = useDnD<Skill, DraggableType>({
    accept: 'skill',
    allItems: allSkills,
    ids: skillIds,
    setIds: setSkillIds,
    userAddedIds: userAddedSkillIds,
    onAdd: onAddSkill,
    onDelete: onDeleteSkill,
    onAddedItemsChange: onAddedSkillsChange,
    onSelectionChange,
    onReset,
    disabled: disabledSelected
  });

  // Add Skill dialog
  const [anchorAddBtn, setAnchorAddBtn] = useState<HTMLButtonElement | null>(null);

  const handleAddSkill = useCallback((skl?: LookupItem | Skill | null) => {
    addItem(skl as Skill);
    setAnchorAddBtn(null);
  }, [addItem]);

  const handleAddSkillGroup = useCallback((sklGrp?: SkillGroup | null) => {
    if (sklGrp) onAddSkillGroup?.(sklGrp);
    setAnchorAddBtn(null);
  }, [onAddSkillGroup]);

  const handleAddOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => event && setAnchorAddBtn(event.currentTarget), []);
  const handleAddClose = useCallback(() => setAnchorAddBtn(null), []);

  return skills ? (
    <>
      <CardSection
          ref={drop as unknown as Ref<Component<BoxProps>>}
          shady
      >
        {variant === SKILL_SELECTOR_TALENT && size(availableIds) >= 1 ? (
          <BoxTypography variant="body2" color="text.secondary" className={sectionHeader}>
            <FormattedMessage id={title} defaultMessage={title} values={values}/>
          </BoxTypography>
        ) : undefined}
        <TeamSkills
            variant={(variant === SKILL_SELECTOR_JOB && 'simple') ||
              (variant === SKILL_SELECTOR_TALENT && 'uniform') ||
              'normal'}
            withLevels={withLevels}
            skillIds={availableIds}
            skills={skills as SkillsStateType}
            addedSkillIds={addedIds}
            onClick={onClick}
            onDelete={deleteItem}
            moveSkill={moveItem}
            onAddSkill={handleAddOpen}
            disabled={disabledSkills}
            pending={disabledAddSkill}
            dragging={isDragging}
            emptyMessage={emptyMessage}
        />
        {variant === SKILL_SELECTOR_TALENT ? undefined : (
          <>
            {warning ? (
              <AlertBox severity="error" variant="standard" elevation={9} mt={2.5}>
                <Typography variant="subtitle2">
                  <FormattedMessage id={warning} defaultMessage={warning} values={values}/>
                </Typography>
              </AlertBox>
            ) : undefined}
            {title || subtitle ? (
              <Box
                  display="flex"
                  alignItems="center"
                  pt={warning ? 2.5 : 5}
                  pb={1.75}
              >
                {title ? (
                  <BoxTypography pr={2.5} variant="subtitle1">
                    <FormattedMessage id={title} defaultMessage={title} values={values}/>
                  </BoxTypography>
                ) : undefined}
                {subtitle ? (
                  <BoxTypography flexGrow={1} variant="subtitle2" fontStyle="italic">
                    <FormattedMessage id={subtitle} defaultMessage={subtitle} values={values}/>
                  </BoxTypography>
                ) : undefined}
              </Box>
            ) : undefined}
            <TeamSelectedSkills
                ref={selectedDrop as unknown as Ref<ConnectableElement>}
                variant={variant === SKILL_SELECTOR_JOB ? 'simple' : 'normal'}
                withLevels={withLevels}
                skillIds={skillIds}
                skills={skills as SkillsStateType}
                moveSkill={moveItem}
                onClick={onClick}
                onDelete={unselectItem}
                onReset={onReset ? resetItems : undefined}
                disabled={disabledSelected}
                isActive={isActive}
            />
          </>
        )}
      </CardSection>
      {variant === SKILL_SELECTOR_TALENT ? (
        <>
          {size(allSkills) >= 1 && (
            <CardSection
                ref={selectedDrop as unknown as Ref<Component<BoxProps>>}
                shady
            >
              <BoxTypography variant="body2" color="text.secondary" className={sectionHeader}>
                <FormattedMessage id={subtitle} defaultMessage={subtitle} values={values}/>
              </BoxTypography>
              <TeamSelectedSkills
                  variant="uniform"
                  withLevels={withLevels}
                  skillIds={skillIds}
                  skills={skills as SkillsStateType}
                  moveSkill={moveItem}
                  onClick={onClick}
                  onDelete={deleteItem}
                  disabled={disabledSelected}
                  isActive={isActive}
              />
            </CardSection>
          )}
          <AddSkillsDialog
              anchorEl={anchorAddBtn}
              exclude={excludeIds}
              excludeJobs={excludeJobIds}
              excludeOrgs={excludeOrgIds}
              onAdd={handleAddSkill}
              onAddGroup={handleAddSkillGroup}
              onCancel={handleAddClose}
              disabled={disabledSkills || disabledSelected}
          />
        </>
      ) : (
        <AddSkillPopover
            supervisor
            exclude={excludeIds}
            anchorEl={anchorAddBtn}
            onAdd={handleAddSkill}
            onCancel={handleAddClose}
            disabled={disabledSkills || disabledSelected}
            SkillSearchComponent={SkillSearch}
        />
      )}
    </>
  ) : null;
};

export default memo(SkillsDnDSelector);
